/**
 * 단계 카드 활성/비활성 판정 함수
 * 권위 문서: docs/console/2026-05-14_corporate-store-opening-procedure.md §6.3
 *
 * §3.4 소방완비 판정 표:
 * | 지하층? | 1층+외부직결? | 면적 | 결과   |
 * | YES    | —           | 66㎡+ | 필요  |
 * | YES    | —           | 66㎡- | 면제  |
 * | NO     | YES         | 무관  | 면제  |
 * | NO     | NO          | 100㎡+ | 필요 |
 * | NO     | NO          | 100㎡- | 면제 |
 */
import type { ToggleState } from './toggleConfig';

/** C(소방완비) 및 4.7(화재배상보험)의 공통 활성 조건 */
function isFireSafetyRequired(t: ToggleState): boolean {
  if (t.isBasement) {
    // 지하층: 66㎡ 이상이면 필요
    return t.isOverAreaThreshold;
  }
  if (t.isGroundFloorDirect) {
    // 1층+외부직결: 항상 면제
    return false;
  }
  // 지상, 외부직결 아님: 100㎡ 이상이면 필요
  return t.isOverAreaThreshold;
}

/**
 * 해당 단계 카드가 현재 토글 상태에서 활성(true)인지 반환.
 * false 면 dim 또는 숨김 처리.
 */
export function isStepActive(stepId: string, t: ToggleState): boolean {
  switch (stepId) {
    // 가맹 트랙 전용
    case 'P2':
    case 'P3':
      return t.businessType === 'franchise';

    // 항상 활성
    case 'P1':
    case 'A':
    case 'D':
    case 'E':
      return true;

    // 지점 설치 등기: 법인 직영 + 상법상 지점 설치 등기 체크박스
    case 'E2':
      return t.businessType === 'corporate' && t.branchRegistration === true;

    // 위생교육: 면허 소지자는 면제 → 면허 없으면 활성
    case 'B':
      return !t.ownerHasLicense;

    // 소방완비: §3.4 판정
    case 'C':
      return isFireSafetyRequired(t);

    // 통신판매업: 온라인/배달앱 운영 시
    case 'F':
      return t.sellsOnline;

    // 추가 인허가
    case '4.1':
      // 주류면허: 주류 판매 AND 일반음식점
      return t.sellsAlcohol && t.foodServiceType === 'general';
    case '4.2':
      // 정화조: 신축·증축·용도변경
      return t.isNewBuildOrRenovation;
    case '4.3':
      // LPG
      return t.usesLPG;
    case '4.4':
      // 옥외광고
      return t.hasLargeSignage;
    case '4.5':
      // 외국인 고용
      return t.hiresForeignWorkers;
    case '4.6':
      // 심야영업 참고: 학교 50m 이내면 제한 안내
      return t.isNearSchool;
    case '4.7':
      // 화재배상보험: C(소방완비)와 동일 조건
      return isFireSafetyRequired(t);

    // PRE-PG: 자사 온라인 주문 시스템이 있을 때
    case 'PRE-PG':
      return t.hasOwnOnlineOrder === true;

    // PRE-FIRE: 방염 대상물품이 있고 소방완비도 필요할 때
    case 'PRE-FIRE':
      return t.hasFlammableMaterials === true && isFireSafetyRequired(t);

    default:
      return true;
  }
}
